import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, Mail, MapPin } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { Eyebrow } from "@/components/site/Sections";
import { VendorForm } from "@/components/preservation/VendorForm";
import { PP_BADGES, PP_SERVICES, PP_CAPABILITY, PP_FLOW } from "@/data/preservation";
import { BRAND } from "@/data/content";

const H2 = ({ children }) => <h2 className="font-display text-3xl font-bold tracking-tight text-[#0B132B] sm:text-4xl">{children}</h2>;
const Wrap = ({ className = "", children, testid, id }) => (
  <section id={id} data-testid={testid} className={`py-20 ${className}`}><div className="mx-auto max-w-7xl px-4 lg:px-8">{children}</div></section>
);

const Hero = () => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-[#F5F3EC]" data-testid="pp-hero">
    <div className="absolute inset-0 grid-texture opacity-40" />
    <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
      <div className="max-w-4xl">
        <Eyebrow>Property Preservation · Central Pennsylvania</Eyebrow>
        <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0B132B] sm:text-5xl lg:text-6xl">Property preservation that protects the asset.</h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">Vertexhub Ventures LLC provides complete property preservation and REO field services across Central Pennsylvania for banks, mortgage servicers, lenders, asset managers, realtors and property managers — executed by our own local crew, documented with timestamped before, during and after photos, and reported within 24 hours.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <CtaButton href="#vendor-packet" size="lg" data-testid="pp-hero-packet">Request Our Vendor Packet</CtaButton>
          <CtaButton href={BRAND.phoneHref} variant="slate" size="lg" data-testid="pp-hero-call"><Phone className="h-4 w-4" /> Call {BRAND.phone}</CtaButton>
        </div>
        <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2" data-testid="pp-badges">
          {PP_BADGES.map((b) => <li key={b} className="flex items-center gap-1.5 text-sm font-medium text-[#0B132B]"><Check className="h-4 w-4 text-gold" /> {b}</li>)}
        </ul>
      </div>
    </div>
  </section>
);

const ServiceCard = ([name, text, to], i) => (
  <article key={name} data-testid={`pp-service-${i}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(11,19,43,0.35)]">
    <span className="font-mono-x text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
    <h3 className="mt-2 font-display text-xl font-bold text-[#0B132B]">{name}</h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{text}</p>
    {to && <Link to={to} data-testid="pp-field-inspections-link" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-gold">See inspection services <ArrowRight className="h-4 w-4" /></Link>}
  </article>
);

const Capability = () => (
  <Wrap className="bg-[#0B132B] text-white" testid="pp-capability">
    <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">For banks, servicers & asset managers</h2>
    <ul className="mt-10 grid gap-x-10 gap-y-5 md:grid-cols-2">
      {PP_CAPABILITY.map((c) => <li key={c} className="flex gap-3 text-sm leading-relaxed text-slate-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {c}</li>)}
    </ul>
  </Wrap>
);

const Flow = () => (
  <Wrap testid="pp-flow">
    <H2>How an order flows</H2>
    <ol className="mt-10 grid gap-5 md:grid-cols-5">
      {PP_FLOW.map((s, i) => (
        <li key={s} className="rounded-2xl border border-slate-200 bg-[#FAF8F1] p-6">
          <span className="font-display text-3xl font-bold text-gold">{i + 1}</span>
          <p className="mt-2 text-sm font-medium text-[#0B132B]">{s}</p>
        </li>
      ))}
    </ol>
  </Wrap>
);

const ContactBlock = () => {
  const o = BRAND.offices[0];
  return (
    <div className="space-y-4 text-sm text-slate-600" data-testid="pp-contact">
      <p className="font-semibold text-[#0B132B]">Vertexhub Ventures LLC</p>
      <a href={o.href} target="_blank" rel="noopener noreferrer" className="flex gap-2 hover:text-gold"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> <span><b>{o.name}:</b> {o.address}</span></a>
      <a href={BRAND.phoneHref} className="flex items-center gap-2 hover:text-gold"><Phone className="h-4 w-4 text-gold" /> {BRAND.phone}</a>
      <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-gold"><Mail className="h-4 w-4 text-gold" /> {BRAND.email}</a>
      <Link to="/inspections" className="inline-flex items-center gap-1 font-semibold text-gold" data-testid="pp-inspections-link">All inspection services <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
};

export default function PropertyPreservation() {
  return (
    <div data-testid="property-preservation-page">
      <Hero />
      <Wrap testid="pp-services">
        <H2>Complete preservation services</H2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{PP_SERVICES.map(ServiceCard)}</div>
      </Wrap>
      <Capability />
      <Flow />
      <Wrap className="bg-[#F5F3EC]" testid="pp-form-section" id="vendor-packet">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <H2>Request our vendor packet or a service quote</H2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">Preservation work is contract-based. Tell us about your portfolio or property and we'll reply with our vendor packet or a quote.</p>
            <div className="mt-8"><ContactBlock /></div>
          </div>
          <VendorForm />
        </div>
      </Wrap>
    </div>
  );
}
